import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, MapPin, Calendar, Plus, Pencil, Trash2, Archive, RefreshCw, AlertTriangle, ShieldAlert, Download } from "lucide-react";
import { maskCnpj } from "@/lib/format";
import DateInputBR from "@/components/DateInputBR";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const emptyCo = {
  razao_social: "", nome_fantasia: "", cnpj: "", cnpj_numerico: "",
  data_fundacao: "", cnae_principal: "", atividade_principal: "", descricao: "",
  telefone: "", email: "", responsavel: "", status: "ativa", logo_url: "",
  endereco: { cep: "", logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "", pais: "Brasil" },
};

export default function CompaniesPage() {
  const { hasRole, user } = useAuth();
  const canEdit = hasRole("rh_admin");
  const canDelete = user?.role === "master";
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCo);
  const [deleteTarget, setDeleteTarget] = useState(null); // { company, links }
  const [deleting, setDeleting] = useState(false);
  const [permanentTarget, setPermanentTarget] = useState(null); // { company, preview }
  const [restoreOpen, setRestoreOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies", { params: includeInactive ? { include_inactive: true } : {} });
      setCompanies(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive]);

  const openNew = () => { setEditing(null); setForm(emptyCo); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...emptyCo, ...c, endereco: { ...emptyCo.endereco, ...(c.endereco || {}) } }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      payload.cnpj_numerico = (payload.cnpj || "").replace(/\D/g, "");
      if (editing) {
        await api.put(`/companies/${editing.id}`, payload);
        toast.success("Empresa atualizada");
      } else {
        await api.post("/companies", payload);
        toast.success("Empresa criada");
      }
      setOpen(false);
      load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const askDelete = async (c) => {
    try {
      const { data } = await api.get(`/companies/${c.id}/links`);
      setDeleteTarget({ company: c, links: data.links });
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const confirmDelete = async (force = false) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/companies/${deleteTarget.company.id}`, {
        params: force ? { force: true } : {},
      });
      toast.success(`Empresa "${deleteTarget.company.nome_fantasia}" inativada`);
      setDeleteTarget(null);
      load();
    } catch (ex) {
      const status = ex.response?.status;
      if (status === 409) {
        // server returned links — show second-step force confirmation
        const detail = ex.response?.data?.detail;
        if (detail?.links) setDeleteTarget((p) => ({ ...p, links: detail.links, blocked: true }));
        toast.error(detail?.message || "Empresa possui vínculos ativos");
      } else {
        toast.error(formatApiError(ex.response?.data?.detail));
      }
    } finally { setDeleting(false); }
  };

  const restoreCompany = async (c) => {
    if (!window.confirm(`Reativar a empresa "${c.nome_fantasia}"?`)) return;
    try {
      await api.post(`/companies/${c.id}/restore`);
      toast.success("Empresa reativada");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Grupo Econômico</div>
          <h1 className="font-display text-3xl font-semibold">Empresas do Grupo ALCON</h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            Cadastre, edite e acompanhe as empresas vinculadas ao grupo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-[12px] text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox" checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              data-testid="company-show-inactive"
              className="accent-[#234935]"
            />
            <Archive className="h-3.5 w-3.5" /> Mostrar inativas
          </label>
          {canEdit && (
            <Button onClick={openNew} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="company-new-btn">
              <Plus className="h-4 w-4 mr-2" /> Nova empresa
            </Button>
          )}
          {canDelete && (
            <Button variant="outline" onClick={() => setRestoreOpen(true)}
                    className="border-slate-300"
                    data-testid="btn-open-restore">
              <RefreshCw className="h-4 w-4 mr-2" /> Restaurar de backup
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          [1, 2, 3].map((i) => <Skeleton key={i} className="h-[230px]" />)
        ) : companies.map((c) => (
          <div
            key={c.id}
            data-testid={`company-card-${c.id}`}
            className="group rounded-lg border border-border bg-white p-5 transition-all hover:-translate-y-[1px] hover:shadow-md"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-md bg-[#F9F4E7] text-[#8A6B2B]">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="leading-tight">
                  <div className="font-display text-[16px] font-semibold">{c.nome_fantasia}</div>
                  <div className="text-[11.5px] text-muted-foreground">{maskCnpj(c.cnpj)}</div>
                </div>
              </div>
              <Badge variant="outline" className={c.status === "ativa" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}>
                {c.status === "ativa" ? "Ativa" : "Inativa"}
              </Badge>
            </div>
            <div className="mt-3 text-[12.5px] text-muted-foreground leading-relaxed">
              {c.razao_social}
            </div>
            <div className="mt-4 space-y-1.5 text-[12.5px]">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                <span className="truncate">
                  {[c.endereco?.logradouro, c.endereco?.cidade, c.endereco?.estado].filter(Boolean).join(" · ")}
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                <span>Fundação: {c.data_fundacao || "—"}</span>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{c.atividade_principal}</span>
              <div className="flex items-center gap-1">
                {canEdit && c.status !== "inativa" && (
                  <Button
                    variant="ghost" size="sm"
                    className="hover:bg-secondary"
                    onClick={() => openEdit(c)}
                    data-testid={`company-edit-${c.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                  </Button>
                )}
                {canDelete && c.status !== "inativa" && (
                  <Button
                    variant="ghost" size="sm"
                    className="text-rose-600 hover:bg-rose-50"
                    onClick={() => askDelete(c)}
                    data-testid={`company-delete-${c.id}`}
                    title="Excluir (inativar) empresa"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canEdit && c.status === "inativa" && (
                  <Button
                    variant="ghost" size="sm"
                    className="text-emerald-700 hover:bg-emerald-50"
                    onClick={() => restoreCompany(c)}
                    data-testid={`company-restore-${c.id}`}
                    title="Reativar empresa"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reativar
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar empresa" : "Nova empresa"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Razão social" value={form.razao_social} onChange={(v) => setForm({ ...form, razao_social: v })} testId="co-razao" />
            <Field label="Nome fantasia" value={form.nome_fantasia} onChange={(v) => setForm({ ...form, nome_fantasia: v })} testId="co-fantasia" />
            <Field label="CNPJ" value={form.cnpj} onChange={(v) => setForm({ ...form, cnpj: v })} testId="co-cnpj" />
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Data de fundação</Label>
              <DateInputBR value={form.data_fundacao} onChange={(v) => setForm({ ...form, data_fundacao: v })} testId="co-fundacao" />
            </div>
            <Field label="CNAE principal" value={form.cnae_principal} onChange={(v) => setForm({ ...form, cnae_principal: v })} />
            <Field label="Atividade principal" value={form.atividade_principal} onChange={(v) => setForm({ ...form, atividade_principal: v })} />
            <Field label="Telefone" value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v })} />
            <Field label="E-mail" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Responsável" value={form.responsavel} onChange={(v) => setForm({ ...form, responsavel: v })} />
            <Field label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} />
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Descrição</Label>
              <Textarea rows={2} value={form.descricao || ""} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Logo da empresa</Label>
              <div className="flex items-start gap-3 mt-1">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="logo" className="h-16 w-16 object-contain rounded border border-border bg-white p-1" data-testid="co-logo-preview" />
                ) : (
                  <div className="h-16 w-16 rounded border border-dashed border-border grid place-items-center text-[10px] text-muted-foreground">sem logo</div>
                )}
                <div className="flex-1 space-y-2">
                  <input type="file" accept="image/*" data-testid="co-logo-input"
                         onChange={(e) => {
                           const f = e.target.files?.[0];
                           if (!f) return;
                           if (f.size > 1024 * 1024) { toast.error("Imagem máx 1MB"); return; }
                           const reader = new FileReader();
                           reader.onload = (ev) => setForm({ ...form, logo_url: ev.target.result });
                           reader.readAsDataURL(f);
                         }}
                         className="text-[11px]" />
                  {form.logo_url && (
                    <Button size="sm" variant="ghost"
                            className="text-rose-600 h-7 text-[11px]"
                            onClick={() => setForm({ ...form, logo_url: "" })}>
                      Remover logo
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <Field label="CEP" value={form.endereco?.cep} onChange={(v) => setForm({ ...form, endereco: { ...form.endereco, cep: v } })} />
            <Field label="Logradouro" value={form.endereco?.logradouro} onChange={(v) => setForm({ ...form, endereco: { ...form.endereco, logradouro: v } })} />
            <Field label="Número" value={form.endereco?.numero} onChange={(v) => setForm({ ...form, endereco: { ...form.endereco, numero: v } })} />
            <Field label="Bairro" value={form.endereco?.bairro} onChange={(v) => setForm({ ...form, endereco: { ...form.endereco, bairro: v } })} />
            <Field label="Cidade" value={form.endereco?.cidade} onChange={(v) => setForm({ ...form, endereco: { ...form.endereco, cidade: v } })} />
            <Field label="Estado" value={form.endereco?.estado} onChange={(v) => setForm({ ...form, endereco: { ...form.endereco, estado: v } })} />
          </div>

          {/* ZONA DE PERIGO — Master only, apenas em edição */}
          {editing && canDelete && (
            <div className="mt-6 rounded-lg border-2 border-rose-300 bg-rose-50/50 p-4"
                 data-testid="danger-zone">
              <div className="flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-display text-[15px] font-semibold text-rose-800 tracking-tight">
                    Zona de Perigo · Excluir empresa definitivamente
                  </h3>
                  <p className="text-[12px] text-rose-700 mt-1 leading-relaxed">
                    Esta operação excluirá <b>permanentemente</b> a empresa e todos os dados
                    vinculados exclusivamente a ela (colaboradores, setores, pesquisas,
                    afastamentos, metas, históricos). Esta ação <b>não poderá ser desfeita</b>.
                    Antes de confirmar, o sistema mostrará uma prévia detalhada do impacto.
                  </p>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="mt-3 bg-rose-600 hover:bg-rose-700"
                    onClick={async () => {
                      try {
                        const { data } = await api.get(`/companies/${editing.id}/deletion-preview`);
                        setOpen(false);
                        setPermanentTarget({ company: editing, preview: data });
                      } catch (ex) {
                        toast.error(formatApiError(ex.response?.data?.detail));
                      }
                    }}
                    data-testid="btn-open-permanent-delete"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" />
                    Excluir empresa definitivamente
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={save} data-testid="co-save-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="company-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" /> Confirmar exclusão
            </DialogTitle>
            <DialogDescription>
              Esta operação irá <b>inativar</b> a empresa (soft-delete). Os dados permanecem
              preservados para histórico e poderão ser reativados.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-3">
              <div className="rounded-md border border-border bg-slate-50 p-3 text-[13px]">
                <div className="font-display font-semibold">{deleteTarget.company.nome_fantasia}</div>
                <div className="text-[11px] text-muted-foreground font-mono">{maskCnpj(deleteTarget.company.cnpj)}</div>
              </div>
              {deleteTarget.links?.total > 0 ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-[12.5px] space-y-1.5">
                  <div className="flex items-center gap-1.5 font-medium text-amber-800">
                    <AlertTriangle className="h-4 w-4" /> Vínculos ativos detectados:
                  </div>
                  <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-amber-900 mt-1.5 ml-1">
                    <li>• Colaboradores ativos: <b>{deleteTarget.links.employees}</b></li>
                    <li>• Setores ativos: <b>{deleteTarget.links.sectors}</b></li>
                    <li>• Cargos: <b>{deleteTarget.links.job_roles}</b></li>
                    <li>• Planos de cargos: <b>{deleteTarget.links.job_plans}</b></li>
                    <li>• Quadros de trabalho: <b>{deleteTarget.links.work_boards}</b></li>
                  </ul>
                  <div className="text-[11.5px] text-amber-800 mt-2">
                    Esses vínculos permanecem no banco de dados após a inativação. Ao reativar a empresa, os vínculos voltam a aparecer normalmente.
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[12.5px] text-emerald-800">
                  Nenhum vínculo ativo. Pode inativar com segurança.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={deleting}
              onClick={() => confirmDelete(deleteTarget?.links?.total > 0)}
              data-testid="company-delete-confirm-btn"
            >
              {deleting ? "Inativando..." :
                deleteTarget?.links?.total > 0 ? "Inativar mesmo assim" : "Confirmar inativação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PERMANENT DELETE — modal profissional em 2 etapas */}
      {permanentTarget && (
        <PermanentDeleteDialog
          target={permanentTarget}
          onClose={() => setPermanentTarget(null)}
          onDeleted={() => { setPermanentTarget(null); load(); }}
        />
      )}

      {/* RESTORE — restaurar empresa a partir de backup */}
      {restoreOpen && (
        <RestoreCompanyDialog
          onClose={() => setRestoreOpen(false)}
          onRestored={() => { setRestoreOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  PermanentDeleteDialog — Zona de Perigo · 2 etapas com texto exato
// ---------------------------------------------------------------------------
function PermanentDeleteDialog({ target, onClose, onDeleted }) {
  const [step, setStep] = useState(1); // 1=review preview, 2=type confirmation
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const co = target.company;
  const preview = target.preview;
  // Normalização idêntica ao backend: NFD → strip diacritics → collapse spaces → uppercase
  const normalize = (s) =>
    (s || "")
      .normalize("NFD")
      .replace(/\p{Mn}/gu, "")
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase();
  const razaoDisplay = (co.razao_social || co.nome_fantasia || "").trim();
  const expected = `EXCLUIR ${razaoDisplay}`;
  const matched = normalize(confirmation) === normalize(expected);

  const doExport = async () => {
    setExporting(true);
    try {
      const resp = await api.get(`/companies/${co.id}/export-package`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([JSON.stringify(resp.data, null, 2)],
                                              { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_${(co.nome_fantasia || co.id).replace(/\W/g, "_")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup exportado.");
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setExporting(false); }
  };

  const doDelete = async () => {
    if (!matched) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/companies/${co.id}/permanent`, {
        data: { confirmation_text: confirmation.trim() },
      });
      if (data.ok) {
        toast.success(`Empresa excluída. ${data.records_removed_total} registro(s) removidos.`);
        // Se a empresa excluída estava no localStorage, limpar
        if (localStorage.getItem("rh_selected_company") === co.id) {
          localStorage.setItem("rh_selected_company", "all");
        }
        onDeleted();
      } else {
        toast.warning(`Exclusão parcial. Verifique o log de auditoria.`);
        onDeleted();
      }
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      // Backend pode retornar objeto {message, expected_pattern}
      if (typeof detail === "object" && detail?.message) {
        toast.error(`${detail.message}${detail.expected_pattern ? ` Esperado: "${detail.expected_pattern}"` : ""}`);
      } else {
        toast.error(formatApiError(detail) || "Não foi possível concluir a exclusão. Nenhum dado foi removido.");
      }
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="permanent-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <ShieldAlert className="h-5 w-5" />
            {step === 1 ? "Confirmar exclusão · Etapa 1 de 2" : "Confirmação final · Etapa 2 de 2"}
          </DialogTitle>
          <DialogDescription>
            Esta ação é <b>irreversível</b>. Revise cuidadosamente antes de prosseguir.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[13px]">
              <div className="font-display font-semibold text-slate-900">{co.razao_social}</div>
              {co.nome_fantasia && <div className="text-[11.5px] text-slate-600">{co.nome_fantasia}</div>}
              <div className="text-[11px] text-slate-500 font-mono mt-0.5">CNPJ: {maskCnpj(co.cnpj)}</div>
            </div>

            <div className="rounded-md border border-rose-300 bg-rose-50/60 p-3">
              <div className="text-[11.5px] uppercase tracking-[0.14em] font-semibold text-rose-800 mb-2">
                Prévia do impacto · dados que serão excluídos
              </div>
              <div className="grid grid-cols-2 gap-2 text-[12px]">
                <div>• Colaboradores: <b>{preview.scope.employees}</b></div>
                <div>• Setores: <b>{preview.scope.sectors}</b></div>
                <div>• Vagas: <b>{preview.scope.vacancies}</b></div>
                <div>• Candidatos: <b>{preview.scope.candidates}</b></div>
              </div>
              <div className="mt-2 max-h-36 overflow-y-auto border-t border-rose-200 pt-2">
                {Object.entries(preview.counts_by_collection || {})
                  .filter(([k]) => k !== "companies")
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => (
                    <div key={k} className="flex justify-between text-[11.5px] py-0.5 text-rose-900">
                      <span className="font-mono">{k}</span>
                      <span className="font-semibold tabular-nums">{v}</span>
                    </div>
                  ))}
              </div>
              <div className="mt-2 pt-2 border-t border-rose-200 flex items-center justify-between text-[13px] font-semibold text-rose-800">
                <span>Total aproximado:</span>
                <span className="tabular-nums">{preview.estimated_total_records} registros</span>
              </div>
            </div>

            {preview.users_impact?.users_shared_will_unlink > 0 && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11.5px] text-emerald-900">
                <b>{preview.users_impact.users_shared_will_unlink} usuário(s) compartilhado(s)</b> perderão
                apenas o vínculo com esta empresa — <b>não serão excluídos</b>.
              </div>
            )}

            <Button variant="outline" size="sm" onClick={doExport} disabled={exporting}
                    className="w-full border-slate-300"
                    data-testid="btn-export-before-delete">
              <Download className="h-4 w-4 mr-1.5" />
              {exporting ? "Exportando..." : "Exportar dados antes de excluir (JSON)"}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="rounded-md border-2 border-rose-300 bg-rose-50 p-3 text-[12.5px] text-rose-900">
              Para confirmar a exclusão definitiva de <b>{co.nome_fantasia || co.razao_social}</b> e
              todos os {preview.estimated_total_records} registros vinculados, digite ou copie exatamente:
              <div className="mt-2 flex items-stretch gap-1.5">
                <div className="flex-1 font-mono text-[12px] bg-white border border-rose-200 rounded px-2 py-1.5 select-all break-all"
                     data-testid="permanent-delete-expected">
                  {expected}
                </div>
                <Button
                  type="button" size="sm" variant="outline"
                  className="h-auto border-rose-300 text-rose-700 hover:bg-rose-100 shrink-0"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(expected);
                      toast.success("Texto copiado.");
                    } catch { toast.error("Não foi possível copiar."); }
                  }}
                  data-testid="btn-copy-expected"
                >
                  Copiar texto
                </Button>
              </div>
              <div className="mt-1 text-[10.5px] text-rose-700/80">
                A comparação é tolerante a maiúsculas/minúsculas, espaços e acentos.
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Confirmação *</Label>
              <Input
                autoFocus
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder={expected}
                className={matched ? "border-emerald-400 focus:ring-emerald-400" : ""}
                data-testid="permanent-delete-input"
              />
              {confirmation && matched && (
                <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
                  <span>✓</span> Confirmação válida
                </div>
              )}
              {confirmation && !matched && (
                <div className="text-[11px] text-rose-600 mt-1">
                  Texto não confere. Digite ou copie exatamente a confirmação indicada acima.
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={deleting}>Cancelar</Button>
          {step === 1 ? (
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={() => setStep(2)}
              data-testid="btn-goto-step2"
            >
              Continuar para confirmação
            </Button>
          ) : (
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={!matched || deleting}
              onClick={doDelete}
              data-testid="btn-permanent-delete-confirm"
            >
              {deleting ? "Excluindo..." : "Excluir definitivamente"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}


// ---------------------------------------------------------------------------
//  RestoreCompanyDialog — Restaurar empresa via JSON de backup (Master only)
// ---------------------------------------------------------------------------
function RestoreCompanyDialog({ onClose, onRestored }) {
  const [step, setStep] = useState(1); // 1=select+validate, 2=confirm+execute
  const [pkg, setPkg] = useState(null);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [forceNewId, setForceNewId] = useState(false);
  const [fileName, setFileName] = useState("");

  const expected = pkg?.company?.razao_social
    ? `RESTAURAR ${pkg.company.razao_social.toUpperCase()}`
    : "";
  const matched = confirmation.trim().toUpperCase() === expected;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setValidation(null);
    setPkg(null);
    try {
      const text = await f.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { toast.error("Arquivo não é JSON válido."); return; }
      setPkg(parsed);
      setValidating(true);
      const { data } = await api.post("/companies/restore/validate", {
        package: parsed, confirmation_text: "preview",
      });
      setValidation(data);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao validar backup.");
    } finally { setValidating(false); }
  };

  const doRestore = async () => {
    if (!matched || !pkg) return;
    setRestoring(true);
    try {
      const { data } = await api.post("/companies/restore", {
        package: pkg,
        confirmation_text: confirmation.trim(),
        force_new_company_id: forceNewId,
      });
      if (data.ok) {
        toast.success(`Empresa restaurada. ${data.records_inserted_total} registros.`);
      } else {
        toast.warning(`Restauração parcial. Verifique o log.`);
      }
      onRestored();
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      if (typeof detail === "object" && detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(formatApiError(detail) || "Falha ao restaurar.");
      }
    } finally { setRestoring(false); }
  };

  const conflicts = validation?.conflicts || [];
  const recordConflicts = validation?.record_conflicts_sample || [];
  const hasConflicts = conflicts.length > 0 || recordConflicts.length > 0;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="restore-company-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#065F46]">
            <RefreshCw className="h-5 w-5" />
            {step === 1 ? "Restaurar empresa · Etapa 1 de 2" : "Confirmar restauração · Etapa 2 de 2"}
          </DialogTitle>
          <DialogDescription>
            Selecione um arquivo <b>.json</b> gerado por <code>Exportar dados antes de excluir</code>. O sistema valida o pacote antes de executar.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Arquivo de backup (JSON) *</Label>
              <input type="file" accept="application/json,.json"
                     onChange={onFile}
                     className="mt-1 block w-full text-[12px] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#065F46] file:text-white file:cursor-pointer"
                     data-testid="restore-file-input" />
              {fileName && <div className="mt-1 text-[11px] text-slate-500">{fileName}</div>}
            </div>

            {validating && (
              <div className="text-[12px] text-slate-500 flex items-center gap-2">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Validando pacote...
              </div>
            )}

            {validation && (
              <>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[13px]">
                  <div className="font-display font-semibold text-slate-900">{validation.company_summary.razao_social}</div>
                  {validation.company_summary.nome_fantasia && (
                    <div className="text-[11.5px] text-slate-600">{validation.company_summary.nome_fantasia}</div>
                  )}
                  <div className="text-[11px] text-slate-500 font-mono mt-0.5">CNPJ: {validation.company_summary.cnpj || "—"}</div>
                  <div className="text-[10.5px] text-slate-400 mt-1">
                    Backup gerado em: {validation.signature.generated_at || "—"}
                  </div>
                </div>

                <div className={`rounded-md border p-3 ${hasConflicts ? "border-rose-300 bg-rose-50/60" : "border-emerald-200 bg-emerald-50"}`}>
                  <div className="text-[11.5px] uppercase tracking-[0.14em] font-semibold mb-2 flex items-center gap-1.5">
                    {hasConflicts ? (
                      <><AlertTriangle className="h-3.5 w-3.5 text-rose-700" /> <span className="text-rose-800">Conflitos detectados</span></>
                    ) : (
                      <><span className="h-2 w-2 rounded-full bg-emerald-500" /> <span className="text-emerald-800">Pacote válido · pronto para restaurar</span></>
                    )}
                  </div>
                  <div className="text-[12px]">
                    <div className="flex justify-between font-semibold border-b border-slate-200 pb-1 mb-1">
                      <span>Total de registros:</span>
                      <span className="tabular-nums">{validation.total_records}</span>
                    </div>
                    <div className="max-h-32 overflow-y-auto">
                      {Object.entries(validation.counts_by_collection || {})
                        .filter(([, v]) => v > 0)
                        .map(([k, v]) => (
                          <div key={k} className="flex justify-between text-[11.5px] py-0.5">
                            <span className="font-mono text-slate-700">{k}</span>
                            <span className="tabular-nums">{v}</span>
                          </div>
                        ))}
                    </div>
                  </div>

                  {conflicts.map((c, i) => (
                    <div key={i} className="mt-2 pt-2 border-t border-rose-200 text-[11.5px] text-rose-900">
                      • <b>{c.type}</b> — {c.collection}
                      {c.existing && <>: já existe como <b>{c.existing}</b></>}
                    </div>
                  ))}
                  {recordConflicts.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-rose-200 text-[11.5px] text-rose-900">
                      <b>{recordConflicts.length} registro(s)</b> com ids já em uso — restauração bloqueada.
                    </div>
                  )}
                </div>

                {conflicts.some((c) => c.type === "company_id_exists") && (
                  <label className="flex items-start gap-2 text-[12px] text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={forceNewId}
                           onChange={(e) => setForceNewId(e.target.checked)}
                           className="mt-0.5 accent-[#065F46]"
                           data-testid="force-new-id" />
                    <span>Restaurar como <b>nova empresa</b> (gerar novo <code>company_id</code>). Marque só se realmente deseja duplicar.</span>
                  </label>
                )}
              </>
            )}
          </div>
        )}

        {step === 2 && validation && (
          <div className="space-y-3">
            <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-3 text-[12.5px] text-emerald-900">
              Para confirmar a restauração de <b>{validation.company_summary.razao_social}</b> ({validation.total_records} registros), digite exatamente:
              <div className="mt-2 font-mono text-[12px] bg-white border border-emerald-200 rounded px-2 py-1.5 select-all">
                {expected}
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Confirmação *</Label>
              <Input autoFocus value={confirmation}
                     onChange={(e) => setConfirmation(e.target.value)}
                     placeholder={expected}
                     className={matched ? "border-emerald-400 focus:ring-emerald-400" : ""}
                     data-testid="restore-confirm-input" />
              {confirmation && !matched && (
                <div className="text-[11px] text-rose-600 mt-1">Texto não confere.</div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={restoring}>Cancelar</Button>
          {step === 1 ? (
            <Button
              className="bg-[#065F46] hover:bg-[#054d38] text-white"
              disabled={
                !validation
                || (recordConflicts.length > 0)
                || (conflicts.some((c) => c.type === "cnpj_exists_in_other_company"))
                || (conflicts.some((c) => c.type === "company_id_exists") && !forceNewId)
              }
              onClick={() => setStep(2)}
              data-testid="btn-goto-restore-step2"
            >
              Continuar para confirmação
            </Button>
          ) : (
            <Button
              className="bg-[#065F46] hover:bg-[#054d38] text-white"
              disabled={!matched || restoring}
              onClick={doRestore}
              data-testid="btn-restore-confirm"
            >
              {restoring ? "Restaurando..." : "Restaurar definitivamente"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
